import { plainToInstance } from 'class-transformer'
import _, { get, orderBy } from 'lodash'
import { getEnv, Instance, SnapshotOut, types } from 'mobx-state-tree'
import { CardString } from 'pokersolver'
import { AppState, AppStateStatus, Platform } from 'react-native'
import { v4 as nanoid } from 'uuid' // 20 seconds
import {
  calculatePlayingStyle,
  formatDate,
  formatNumber,
  GameType,
  isRegistrationClosed,
  percentage,
  Timing
} from '@ppwr/poker'

import {
  BetHint,
  BetKind,
  Blind,
  BoardStates,
  BoardStateTag,
  BOT_KINDS,
  Deck,
  // defaultPlayerActionTimeQuickPlay,
  // defaultPlayerActionTimeTournament,
  GAME_CONFIG,
  GameState,
  GameStates,
  LastAction,
  Player,
  PlayerKind,
  PlayerStates,
  Pot,
  PotWinner,
  TableEventKind,
  TableSnapshot,
  THREE_CARD_BOT_NAMES
} from '@ppwr/poker'

import { getActiveRouteName } from '../../navigation'
import * as storage from '../../../app/utils/storage'
import { MappingError } from '../../utils/errors/errors.mapping'
import { extendLogger, LoggerContexts } from '../../utils/logger'
import {
  BotKind,
  channelModelPrimitives,
  gameEntityModelPrimitives,
  GameEntityModelType,
  GameModeTag,
  GameSnapshotModel,
  GameSnapshotModelType,
  PlayerStatsEntityModel,
  PlayerStatsEntityModelType,
  ChannelModel,
  ThreeCardDailyGameStatsModel,
  ThreeCardDailyGameStatsModelType,
  TournamentAccessControl,
  TournamentDifficulty,
  GameStatsModel,
  GameStatsModelType,
  ChannelModelType,
  TournamentModelType,
  tournamentModelPrimitives,
  gameSnapshotModelPrimitives,
  CanPlayThreeCardDailyGameModelType,
  CanPlayThreeCardDailyGameModel,
  UserGameScheduleResponseModelType,
  UserRoles,
  playerStatsEntityModelPrimitives,
  playerGameStatsModelPrimitives,
  PlayerGameStatsModelType,
  playerStatBiggestWinModelPrimitives,
  PlayerGameStatsModel
} from '../api-store'
import {
  BetCommand,
  BlindCommand,
  BuyCommand,
  InputEditUserGameSchedule,
  TableCommand
} from '../api-store/RootStore.base'
import { withRootStore } from '../extensions'
import { withKinesisGameEventCommands } from '../extensions/withKinesisGameEventCommands'
import { RootStoreEnv } from '../RootStoreEnv'
import { GameEventQueue } from './game-events'
import { GameModalQueue } from './game-modals'
import { translate } from '../../i18n'
import { getLastActionTextForPlayer } from '../../components/ANewDesign/utils'
import { WebSocketConnectionState } from '../network'
import MyFirstGameStepNames from './first-game/MyFirstGameStepNames'
// import { NAVIGATION_PERSISTENCE_KEY } from '../../components/AppBase'

export const defaultPlayerActionTimeQuickPlay = 60 // 1 minute
export const defaultPlayerActionTimeTournament = 30

let interval: number

const logger = extendLogger(LoggerContexts.Game)

export enum PlayKind {
  observe = 'observe',
  play = 'play'
}

export const LastActionModel = types.model('LastActionModel').props({
  amount: types.number,
  action: types.string
})

export const RelativePosition = types.model('RelativePosition').props({
  x: types.number,
  y: types.number
})

// TODO: deprecate this
export const ShareSheetModel = types.model('ShareSheet').props({
  title: types.optional(types.string, ''),
  shareUrl: types.optional(types.string, '')
})

export const PlayerStateModel = types.model('PlayerStateModel').props({
  description: types.string,
  tag: types.string
})

type ShareSheetType = Instance<typeof ShareSheetModel>

export interface PlayerStateType extends Instance<typeof PlayerStateModel> {}

export const PlayerModel = types.model('PlayerModel').props({
  id: types.identifier,
  actions: types.optional(types.array(types.string), ['blank', 'blank', 'blank']),
  player: types.maybe(types.frozen<Player>()),
  index: types.number,
  mappedIndex: types.number,
  bet: types.number,
  bets: types.maybe(types.frozen()),
  cards: types.optional(types.array(types.enumeration<CardString>(new Deck().buildDeck())), []),
  chips: types.number,
  name: types.string,
  userId: types.string,
  role: types.string,
  color: types.string,
  buyIn: types.maybe(types.boolean),
  downGradeState: types.optional(types.string, ''),
  // table
  dealer: types.number,
  bigBlindPlayer: types.number,
  smallBlindPlayer: types.number,
  currentPlayer: types.number,
  myPlayer: types.number,
  playerState: PlayerStateModel,
  // playerState: types.maybe(types.frozen()),
  lastAction: types.optional(LastActionModel, { amount: 0, action: '' }),
  kind: types.optional(types.string, PlayerKind.HUMAN)
})

export const timeDurationModel = types.model('timeDurationModel').props({
  hours: types.number,
  minutes: types.number,
  seconds: types.number
})

export const bestHandModel = types.model('bestHandModel').props({
  cards: types.optional(types.array(types.string), []),
  playerId: types.string,
  playerName: types.string,
  chipsWon: types.number,
  rankedHandName: types.string,
  rankedHandDescription: types.string,
  winningCards: types.optional(types.array(types.string), []),
  kind: types.string,
  potKind: types.string,
  userId: types.string
})

export const PlayHistoryModel = types.model('PlayHistoryModel').props({
  createdAt: types.string,
  numberOfHands: types.number,
  timeDuration: timeDurationModel,
  bestHand: types.maybeNull(bestHandModel)
})

export interface PlayHistoryModelType extends Instance<typeof PlayHistoryModel> {}

export interface PlayerType extends Instance<typeof PlayerModel> {}

export const NextBlindModel = types.model('NextBlindModel').props({
  big: types.number,
  small: types.number
})

export interface NextBlindType extends Instance<typeof NextBlindModel> {}

const defaultActionTime = defaultPlayerActionTimeQuickPlay * 1000

/**
 * A GamesStore model.
 */

export const GameModel = types
  .model('GameModel')
  .props({
    allIn: types.optional(types.string, ''),
    // events and modal are imported here. Is it necessary to import them in the game store?
    events: types.optional(GameEventQueue, {}),
    game: types.maybe(GameSnapshotModel),
    myUserId: types.maybe(types.string),
    modals: types.optional(GameModalQueue, {}),
    // winners: types.optional(types.array(types.frozen<PotWinner>()), []),
    showWinningHandLottie: types.optional(types.boolean, false),
    // @TODO: This is the player's option modal that enables video and teacher controls
    isOptionModalEnabled: types.optional(types.boolean, true),
    isFourColoredDeckEnabled: types.optional(types.boolean, false),
    showCardsForAllin: types.optional(types.boolean, false),
    winnerCommunityCards: types.optional(types.array(types.enumeration<CardString>(new Deck().buildDeck())), []),
    isCardsVisible: types.optional(types.boolean, false),
    playerUserIdForTeacher: types.optional(types.string, ''),
    isGameAutostartEnabled: types.optional(types.boolean, false),
    myPlayerStats: types.maybe(PlayerStatsEntityModel),
    myPlayerGameStats: types.maybe(PlayerGameStatsModel),
    hasUserPreFold: types.optional(types.boolean, false),
    pauseGameTimestamp: types.optional(types.number, 0),
    gameResetTimestamp: types.optional(types.number, 0),
    isAnyPlayerOutOfChips: types.optional(types.boolean, false),
    isGameDeleted: types.optional(types.boolean, false),
    gameId: types.optional(types.string, ''),
    gameBegan: types.optional(types.boolean, false),
    currentBlind: types.optional(NextBlindModel, { big: 0, small: 0 }),
    nextBlind: types.optional(NextBlindModel, { big: 0, small: 0 }),
    currentBlindTimestamp: types.optional(types.number, Date.now()),
    gameKind: types.optional(
      types.enumeration<GameModeTag>('GameKind', Object.values(GameModeTag)),
      GameModeTag.default
    ),
    shouldHighlightCurrentPlayer: types.optional(types.boolean, false),
    actionTime: types.optional(types.number, defaultActionTime),
    showPlayerActionTimer: types.optional(types.boolean, true),
    hasUserDeclinedToBuy: types.optional(types.boolean, false),
    remainingTime: types.optional(types.number, 300),
    gameStartTime: types.optional(types.number, 0),
    blindLevel: types.optional(types.number, 0),
    leaderboardData: types.maybe(GameStatsModel),
    threeCardResult: types.array(ThreeCardDailyGameStatsModel),
    canPlayThreeCard: types.maybe(CanPlayThreeCardDailyGameModel),
    isLeaderboardAccessedFromDrawer: types.optional(types.boolean, false),
    myRank: types.optional(types.number, 0),
    isWinnerInfoDisplayedToOutPlayer: types.optional(types.boolean, false),
    isLeaderboardDisplayedToOutPlayer: types.optional(types.boolean, false),
    hasUserTappedOnAction: types.optional(types.boolean, false),
    isFirstTime: types.optional(types.boolean, true),
    pauseRemainingTime: types.optional(types.number, 0),
    isFirstTimeLostMoney: types.optional(types.boolean, true),
    isWinningModalAutoClose: types.optional(types.boolean, true),
    pauseCounter: types.optional(types.number, 0),
    iGameBackgrounded: types.optional(types.boolean, false),
    playerRanks: types.optional(types.array(types.string), []),
    playKind: types.optional(types.string, ''),
    lastActiveCheckpoint: types.optional(types.number, Date.now()),
    observerPlayerNumber: types.optional(types.array(types.number), []),
    isLeaderBoardModalAutoClose: types.optional(types.boolean, true),
    playerJoinedTimeStamp: types.optional(types.number, 0),
    // isLeaderboardDisplay: types.optional(types.string, ''),
    isGameAutoPaused: types.optional(types.boolean, false),
    handWinnerEventStartTime: types.maybe(types.Date),
    showPlayerTimerAfterJoinedGame: types.optional(types.boolean, false),
    tableName: types.optional(types.string, ''),
    showChatOverlay: types.optional(types.boolean, false),
    botsCount: types.optional(types.number, 0),
    showConfirmLoader: types.optional(types.boolean, false),
    showCancelLoader: types.optional(types.boolean, false),
    showSidePots: types.optional(types.boolean, false),
    showChats: types.optional(types.boolean, false),
    potChipsPosition: types.optional(RelativePosition, { x: 0, y: 0 }),
    chipsAnimationStarted: types.optional(types.boolean, false),
    shouldPlayChipsSound: types.optional(types.boolean, false),
    playHistoryData: types.optional(types.array(PlayHistoryModel), []),
    routeBeforeCallingJoinGame: types.maybe(types.string),
    shouldStartFetchGameStateInterval: types.optional(types.boolean, false),
    shouldCancelFetchGameStateInterval: types.optional(types.boolean, false),
    shareSheet: types.maybe(ShareSheetModel),
    showShareSheet: types.optional(types.boolean, false),
    timerCountdown: types.maybe(types.number),
    isBetModalOpen: types.optional(types.boolean, false),
    isCallOptionsModalOpen: types.optional(types.boolean, false),
    isGameStartSheetOpen: types.optional(types.boolean, false),
    landedGameScreenForFirstTime: types.optional(types.boolean, true),
    playerSmallVideoPosition: types.optional(RelativePosition, { x: 0, y: 0 }),
    previewVideoWasShown: types.optional(types.boolean, false),
    isHandWinnersEvent: types.optional(types.boolean, false),
    isTournamentGameOver: types.optional(types.boolean, false)
  })
  .extend(withRootStore)
  .extend(withKinesisGameEventCommands)
  .volatile((self) => {
    const avatarMap: Record<string, JSX.Element> = {}
    return {
      appState: AppState.currentState,
      wasLocalUserVideoHidden: true,
      avatarMap
    }
  })
  .views((self) => ({
    get amountOfHands() {
      return self.game?.table[0].amountOfHands ?? 1
    },
    get communityCards(): CardString[] {
      return self.game?.table[0].communityCards ?? []
    },
    get players(): Player[] {
      // TODO: PlayerModel generated from Graph or Player from poker lib?
      return self.game?.players.map((p: unknown) => plainToInstance(Player, p)) ?? []
    },
    get biggestBet(): number | undefined {
      return self.game?.table[0].biggestBet
    },
    get minimumRaise(): number | undefined {
      return self.game?.table[0].minimumRaise
    },
    get callAmount() {
      return self.game?.table[0].callAmount
    },
    get mainPot(): number | undefined {
      return self.game?.table[0].mainPot
    },
    get sidePots(): Pot[] {
      return self.game?.table[0].sidePots.map((pot: unknown) => plainToInstance(Pot, pot)) ?? []
    },
    get gameState(): GameState | undefined {
      return self.game?.table[0].state
    },
    get boardState(): BoardStateTag | undefined {
      return self.game?.table[0].boardState
    },
    get playersOnlyCalledBB(): boolean | undefined {
      return self.game?.table[0].playersOnlyCalledBB
    },
    get hints(): BetHint[] {
      return self.game?.table[0].betHints ?? []
    },
    get winners(): PotWinner[] {
      return self.game?.table[0].winners ?? []
    },
    get finalWinnerInfo(): { communityCards: CardString[]; player: PotWinner } {
      return self.game?.table[0].finalWinnerInfo
    },
    get updatedCurrentBlinds(): Blind | undefined {
      return self.game?.table[0].updatedCurrentBlinds
    }
  }))
  .views((self) => ({
    get bots() {
      return self.players.filter((player) => player.kind === PlayerKind.BOT)
    },
    get myPlayer(): Player | undefined {
      let id = ''
      if (self.rootStore.game.isWalkthrough) {
        id = 'bettina'
      } else {
        id = self.rootStore.session.user?._id ?? ''
      }
      return self.players.find((p) => p.userId === id)
    },
    get observers() {
      return self.players.filter((p) => p.state.tag === PlayerStates.Observer.tag)
    },
    get nonSeatedObservers() {
      return self.players.filter((p) => {
        const isNotSeatedObserver = p.observerId !== 1
        return p.state.tag === PlayerStates.Observer.tag && isNotSeatedObserver
      })
    },
    get seatedPlayers() {
      return self.players.filter((p) => p.state.tag !== PlayerStates.Observer.tag)
    },
    get seatedPlayersWithSeatedObserver() {
      return self.players.filter((p) => p.state.tag !== PlayerStates.Observer.tag || p.observerId === 1)
    },
    isPlayerInPlay(player: Player) {
      return !(
        player.isFolded() ||
        player.isForeverOut() ||
        player.isDisconnected() ||
        player.isOut() ||
        player.isSittingOut() ||
        player.isWaiting()
      )
    },
    applyOpacityForPlayer(player: Player): boolean {
      return !(self.rootStore.game.isPlayerInPlay(player) || self.rootStore.game.isWalkthrough)
    },
    applyOpacityForFirstGame(): boolean {
      return (
        self.rootStore.game.isMyFirstGame &&
        [MyFirstGameStepNames.roles, MyFirstGameStepNames.beforeWinnerNote, MyFirstGameStepNames.winnerNote].includes(
          self.rootStore.game.stageInternalName
        )
      )
    }
  }))
  .views((self) => ({
    get seatedObserver() {
      return self.observers.find((p) => p.observerId === 1)
    },
    get isMyPlayerTeacher() {
      return self.myPlayer?.userRoles?.includes(UserRoles.teacher)
    },
    get isMyPlayerOutOfChips() {
      return (
        self.myPlayer?.state.tag !== PlayerStates.Observer.tag && self.myPlayer?.chips === 0 && self.myPlayer?.buyIn
      )
    },
    get isMyPlayerOut() {
      if (!self.myPlayer) return false
      const states: string[] = [
        PlayerStates.Out.tag,
        PlayerStates.Waiting.tag,
        PlayerStates.SitOut.tag,
        PlayerStates.ForeverOut.tag
      ]
      return states.includes(self.myPlayer.state.tag)
    },
    get isMyPlayerObserver(): boolean {
      return self.myPlayer?.state.tag === PlayerStates.Observer.tag
    },
    get isMyPlayerFoldedOrWaiting() {
      if (!self.myPlayer) return false
      const states: string[] = [PlayerStates.Out.tag, PlayerStates.Waiting.tag, PlayerStates.Folded.tag]
      return states.includes(self.myPlayer.state.tag)
    },
    get myHoleCards() {
      return self.myPlayer?.cards ?? []
    },
    get myPlayingStatus() {
      return self.myPlayer?.state.tag ?? ''
    },
    get showImBack() {
      if (!self.myPlayer) return false
      const imBackStates = [PlayerStates.ForeverOut.tag, PlayerStates.SitOut.tag] as string[]
      return imBackStates.includes(self.myPlayer.state.tag)
    },
    get seatedPlayersLastActions() {
      return self.seatedPlayers.map((p) => p.lastAction?.action).filter(Boolean) as LastAction['action'][]
    },
    get isMyPlayerWaiting() {
      return self.myPlayer?.state.tag === PlayerStates.Waiting.tag
    },
    get isMyCardsVisibleByTeacher() {
      return !!self.myPlayer?.isCardsVisibleByTeacher
    }
  }))
  .views((self) => ({
    get isDefaultMode() {
      return self.gameKind === GameModeTag.default
    },
    get isCommunityGame(): boolean {
      return (
        self.gameKind === GameModeTag.tournament &&
        self.rootStore.game.tournamentAccessControl === TournamentAccessControl.community
      )
    },
    get isTournamentModeOn() {
      return self.gameKind === GameModeTag.tournament || self.gameKind === GameModeTag.practiceTournament
    },
    get isPrivateGame(): boolean {
      return (
        self.gameKind === GameModeTag.default ||
        (self.gameKind === GameModeTag.tournament &&
          self.rootStore.game.tournamentAccessControl === TournamentAccessControl.private)
      )
    },
    get isPracticeTournamentMode() {
      return self.gameKind === GameModeTag.practiceTournament
    },
    get isPracticeMode() {
      return self.gameKind === GameModeTag.practice
    },
    get isOpenTable(): boolean {
      return (
        self.gameKind === GameModeTag.openQuickGame ||
        (self.gameKind === GameModeTag.tournament &&
          self.rootStore.game.tournamentAccessControl === TournamentAccessControl.public)
      )
    },
    get isOpenTournament(): boolean {
      return (
        self.gameKind === GameModeTag.tournament &&
        self.rootStore.game.tournamentAccessControl === TournamentAccessControl.public
      )
    },
    get isThreeCard() {
      return self.gameKind === GameModeTag.threeCard
    },
    get defaultTable(): TableSnapshot {
      return self.game?.table?.['0'] ?? null
    },
    get observersPlayerName() {
      return self.observers.map((o) => o.name)
    },
    get isAVCallAccessible() {
      const myUserId = self.rootStore.session.user?._id
      const seatedPlayer = self.seatedPlayersWithSeatedObserver.filter((player) => player.userId === myUserId)
      if (seatedPlayer.length > 0) {
        return true
      }
      return false
    },
    get isMyPlayerSeated() {
      const players = self.seatedPlayers
      const myUserId = self.rootStore.session.user?._id
      const myPlayer = players.filter((player) => player.userId === myUserId)
      if (myUserId && players.length > 0 && myPlayer.length === 1) {
        return true
      }
      return false
    },
    get isMyTurn() {
      const currentPlayerId = get(self.game, 'table[0].currentPlayerId', -1)
      return self.myPlayer?.id === currentPlayerId
    },
    get myActions() {
      const playerState = self.game?.forPlayer?.state
      const playerActions = self.game?.forPlayer?.actions

      if (!(playerActions && playerState) || playerState === PlayerStates.Waiting.tag) return []
      const currentPlayerActionHints = self.game?.currentPlayerAction ?? []

      return playerActions.length === 0 ? currentPlayerActionHints : playerActions
    },
    get playerActionTimeLeft() {
      return self.game?.table[0].playerActionTimeLeft
    },
    get chipsCountOnGameReset() {
      return self.myPlayer?.chips
    },
    get blindTimerCheckpoint() {
      const currentTimestamp = Date.now()
      const checkpoint = get(self.game, 'table[0].blindTimerCheckpoint', currentTimestamp)
      return checkpoint
    }
  }))
  .views((self) => ({
    get shouldPlayerSeeStartBottomModal() {
      if (self.isThreeCard) return false
      if (
        self.isOpenTournament ||
        self.isPracticeMode ||
        self.isPracticeTournamentMode ||
        self.gameKind === GameModeTag.openQuickGame
      )
        return true
      if (self.isMyPlayerTeacher) return true
      return false
    },
    get botsAlreadyJoined() {
      return Boolean(self.players.find((p) => p.kind === PlayerKind.BOT))
    },
    get isPreGame() {
      return (self.game?.gameState?.tag ?? '') === GameStates.PreGame.tag
    },
    get isGameBegin(): boolean {
      return (
        !self.rootStore.game.isCardsVisibleOnShowDown &&
        self.seatedPlayers.length > 1 &&
        this.isPlayingGame &&
        !this.isGameReset
      )
    },
    get isGamePaused() {
      return (
        (self.game?.gameState?.tag ?? '') === GameStates.Paused.tag ||
        self.game?.table?.['0'].state?.tag === GameStates.Paused.tag ||
        self.game?.table?.['0'].state?._tag === GameStates.Paused.tag
      )
    },
    get isGameReset() {
      return (
        (self.game?.gameState?.tag ?? '') === GameStates.Reset.tag ||
        self.game?.table?.['0'].state?.tag === GameStates.Reset.tag ||
        self.game?.table?.['0'].state?._tag === GameStates.Reset.tag
      )
    },
    get isGamePlaying() {
      return (
        (self.game?.gameState?.tag ?? '') === GameStates.Playing.tag ||
        self.game?.table?.['0'].state?.tag === GameStates.Playing.tag ||
        self.game?.table?.['0'].state?._tag === GameStates.Playing.tag
      )
    },
    get isGameReadyToPlay() {
      return (
        (self.game?.gameState?.tag ?? '') === GameStates.ReadyToPlay.tag ||
        self.game?.table?.['0'].state?.tag === GameStates.ReadyToPlay.tag ||
        self.game?.table?.['0'].state?._tag === GameStates.ReadyToPlay.tag
      )
    },
    get isGameOver() {
      // const countPlayersOut = self.players.filter((item) => item.chips === 0)
      // logger.log('isGameOver isTournamentModeOn', self.isTournamentModeOn)
      // logger.log('isGameOver countPlayersOut', countPlayersOut.length)
      // logger.log('isGameOver seatedPlayers.length', self.seatedPlayers.length)
      // tournament is over
      // const isAllPlayersOut = countPlayersOut.length + 1 === self.seatedPlayers.length
      // logger.log('isGameOver isAllPlayersOut', isAllPlayersOut)
      // const flag = self.isTournamentModeOn && isAllPlayersOut && this.isPlayingGame === false
      // logger.log('isGameOver flag', flag)
      // return flag
      if (self.defaultTable) {
        const tableState =
          self.defaultTable.state.tag || self.defaultTable.state.tag || self.game?.table?.['0'].state?._tag
        return (self.game?.gameState?.tag ?? '') === GameStates.Finished.tag || tableState === GameStates.Finished.tag
      }
      return (self.game?.gameState?.tag ?? '') === GameStates.Finished.tag
    },
    get isPlayingGame() {
      // return (self.game?.gameState?.tag ?? '') === GameStates.Playing.tag
      const currentGameState = self.game?.gameState?.tag ?? self.defaultTable?.state.tag ?? ''
      const GameStatesArray = [GameStates.ReadyToPlay.tag.toString(), GameStates.Finished.tag.toString()]
      return currentGameState.length > 0 && !GameStatesArray.includes(currentGameState)
    },
    get shouldPrefoldButtonVisible() {
      const tableBiggestBet = self.rootStore.game.getBiggestBet()
      const myData = self.myPlayer
      if (myData) {
        if (myData.bet !== tableBiggestBet && tableBiggestBet > 0) {
          return true
        }
        return false
      }
      return true
    },
    get shouldShowProgressBar(): boolean {
      const PlayingBoardStates = [
        BoardStates.Deal.tag,
        BoardStates.Flop.tag,
        BoardStates.Turn.tag,
        BoardStates.River.tag
      ] as (string | undefined)[]

      return (
        !self.rootStore?.game?.isCardsVisibleOnShowDown &&
        PlayingBoardStates.includes(self.boardState) &&
        !self.rootStore?.game?.isGamePaused &&
        !self.rootStore?.game?.isGameOver &&
        !self.rootStore?.game.isGameReset &&
        !self.hasUserTappedOnAction &&
        self.showPlayerTimerAfterJoinedGame
      )
    },
    get isCurrentGameATeachingTable(): boolean {
      const currentTeachingTableExists = self.rootStore.game?.allDataTeachingTable.find(
        (tt) => tt.channelName === self.rootStore.communication?.channel?.channelName
      )
      return Boolean(currentTeachingTableExists)
    },
    get totalChipsOnTable() {
      const chips = self.defaultTable?.totalChips ?? 0
      return chips
    },
    get currentPlayerId() {
      return self.game?.table[0].currentPlayerId
    },
    get isRegistrationOver(): boolean {
      if (!self.defaultTable?.gameStartTime) return false

      const tournamentJoinTime = self.rootStore.environment.isProd() ? 15 : 10
      const tournamentClosed = isRegistrationClosed(
        self.defaultTable.gameStartTime,
        self.defaultTable.checkpoint,
        tournamentJoinTime
      )

      return !!(self.isTournamentModeOn && self.defaultTable.gameStartTime && tournamentClosed)
    },
    getIsAnyPlayerOutOfChips() {
      if (self.seatedPlayers.length > 0) {
        const playerOutOfChips = self.seatedPlayers.filter((data) => !data.chips && data.buyIn)
        return playerOutOfChips.length > 0
      }
      return false
    },
    getBigBlindPlayerIndex() {
      return self.defaultTable?.bigBlindPlayer ?? 0
    },
    getSmallBlindPlayerIndex() {
      return self.defaultTable?.smallBlindPlayer ?? 0
    },
    getDealerIndex() {
      return self.defaultTable?.buttonPlayer ?? 0
    },
    getcurrentPlayerIndex() {
      return self.defaultTable?.currentPlayer ?? 0
    },
    getBiggestBet() {
      return self.defaultTable?.biggestBet ?? 0
    },
    getSecondsPassed() {
      const currentCheckpoint = Date.now()

      if (self.rootStore.game.isGameReset) {
        const timeLeft = Math.ceil(
          (GAME_CONFIG.DELAY_BUY_IN * 1000 - (currentCheckpoint - self.gameResetTimestamp)) / 1000
        )
        return Math.min(30, Math.max(timeLeft + 5, 0))
      }
      const activePlayers = self.seatedPlayers.filter((player) => player.state.tag !== PlayerStates.Waiting.tag)
      const activeSortedPlayers = orderBy(activePlayers, 'player.checkpoint')
      const lastActiveCheckpoint =
        activeSortedPlayers && activeSortedPlayers.length > 0
          ? (activeSortedPlayers[activeSortedPlayers.length - 1].checkpoint ?? currentCheckpoint)
          : currentCheckpoint

      // DEBUG: Saved logs
      // // logger.log(
      //   '[game] getSecondsPassed gameResetTimestamp',
      //   self.gameResetTimestamp,
      //   new Date(self.gameResetTimestamp)
      // )
      // logger.log('[game] getSecondsPassed pauseGameTimestamp', self.pauseGameTimestamp)
      // logger.log('[game] getSecondsPassed lastActiveCheckpoint', lastActiveCheckpoint)
      // logger.log('[game] getSecondsPassed lastActiveCheckpoint utc', new Date(lastActiveCheckpoint))
      // const utc = new Date(lastActiveCheckpoint)
      // const local = utc.getTime() + utc.getTimezoneOffset() * 60 * 1000
      // // logger.log('[game] getSecondsPassed lastActiveCheckpoint local?', local, new Date(local))
      // logger.log('[game] getSecondsPassed currentCheckpoint', currentCheckpoint, new Date(currentCheckpoint))

      const pauseTimestamp = self.pauseGameTimestamp
      if (currentCheckpoint && lastActiveCheckpoint) {
        if (pauseTimestamp > lastActiveCheckpoint) {
          const diff = Math.abs(pauseTimestamp - lastActiveCheckpoint)
          return diff
        }
        const diff = Math.abs(currentCheckpoint - lastActiveCheckpoint)
        return diff + 10
      }
      return 0
    },
    get isCardsVisibleByTeacher(): boolean {
      const tableData = self.defaultTable
      if (tableData) {
        return tableData.isCardsVisibleByTeacher
      }
      return false
    },
    get isCardsVisibleByEveryone(): boolean {
      const tableData = self.defaultTable
      if (tableData) {
        return tableData.isCardsVisibleByEveryone
      }
      return false
    },
    get isCardsVisibleOnShowDown(): boolean {
      const tableData = self.defaultTable
      if (tableData) {
        return tableData.isCardsVisibleOnShowDown
      }
      return false
    },
    get isDealerSitting() {
      const totalPlayers = self.seatedPlayers.length
      const outOfChipsPlayers = self.seatedPlayers.filter((player) =>
        [PlayerStates.Out.tag.toString(), PlayerStates.Waiting.tag.toString()].includes(player.state.tag)
      )
      if (totalPlayers - outOfChipsPlayers.length === 2) {
        return false
      }
      return true
    }
  }))
  .views((self) => ({
    get myHandsStats() {
      const totalNumberOfHands = self.myPlayerStats?.numberOfHands ?? 0
      const numberOfHandsPlayed = self.myPlayerStats?.numberOfHandsPlayed ?? 0
      const numberOfHandsWon = self.myPlayerStats?.numberOfHandsWon ?? 0
      const preflopFold = self.myPlayerStats?.preflopFold ?? 0
      return {
        totalNumberOfHands: formatNumber(totalNumberOfHands),
        numberOfHandsPlayed: formatNumber(numberOfHandsPlayed),
        numberOfHandsWon: formatNumber(numberOfHandsWon),
        preflopFold: formatNumber(preflopFold),
        percentage: {
          numberOfHandsPlayed: percentage(numberOfHandsPlayed, totalNumberOfHands),
          numberOfHandsWon: percentage(numberOfHandsWon, totalNumberOfHands),
          preflopFold: percentage(preflopFold, totalNumberOfHands)
        }
      }
    },
    get gameTypeText() {
      switch (self.myPlayerGameStats?.gameType) {
        case GameType.communityTournament:
          return translate('gameType.community')
        case GameType.privateQuickPlay:
          return translate('gameType.privateQuickGame')
        case GameType.openQuickGame:
          return translate('gameType.openQuickGame')
        case GameType.practice:
        case GameType.practiceTournament:
          return translate('gameType.practice')
        case GameType.privateTournament:
          return translate('gameType.privateTournament')
        case GameType.openTournament:
          return translate('gameType.openTournament')
      }
      return ''
    },
    get myGameStats() {
      const percentage = (n: number, total: number) => {
        const res = n / total

        if (!Number.isFinite(res)) {
          return 0
        }

        return parseFloat((res * 100).toFixed(2))
      }
      const totalNumberOfHands = self.myPlayerGameStats?.numberOfHands ?? 0
      const numberOfHandsPlayed = self.myPlayerGameStats?.numberOfHandsPlayed ?? 0
      const numberOfHandsWon = self.myPlayerGameStats?.numberOfHandsWon ?? 0
      const preFlopFold = self.myPlayerGameStats?.numberOfPreFlopFolds ?? 0
      const winsAtShowdown = self.myPlayerGameStats?.numberOfHandsWonAtShowdown ?? 0

      const numberOfHandsWonAtShowdownPercentage = percentage(winsAtShowdown, numberOfHandsPlayed)
      const preFlopFoldPercentage = percentage(preFlopFold, totalNumberOfHands)
      const winPercentage = percentage(numberOfHandsWon, totalNumberOfHands)
      const handsPlayedPercentage = percentage(numberOfHandsPlayed, totalNumberOfHands)

      return {
        numberOfHands: formatNumber(totalNumberOfHands),
        numberOfHandsPlayed: formatNumber(numberOfHandsPlayed),
        numberOfHandsWon: formatNumber(numberOfHandsWon),
        percentage: {
          handsWon: Math.round(winPercentage),
          handsPlayed: Math.round(handsPlayedPercentage),
          preFlopFold: Math.round(preFlopFoldPercentage),
          winsAtShowdown: Math.round(numberOfHandsWonAtShowdownPercentage)
        },
        playingStyle: calculatePlayingStyle(preFlopFoldPercentage, numberOfHandsWonAtShowdownPercentage),
        gameType: this.gameTypeText,
        gameStartTime: self.myPlayerGameStats?.gameStartTime
      }
    },
    get myAllTimeBest() {
      const allTimeBiggestChipsWon = self.myPlayerStats?.allTimeBiggestWin?.chipsWon ?? 0
      const allTimeBiggestWinDate = self.myPlayerStats?.allTimeBiggestWin?.createdAt ?? 0
      const allTimeBestHandWon = self.myPlayerStats?.allTimeBestHandWon ?? ''
      const bestHand =
        allTimeBestHandWon && 'winningCards' in allTimeBestHandWon
          ? (allTimeBestHandWon.winningCards as CardString[])
          : []
      const bestHandName = allTimeBestHandWon ? allTimeBestHandWon.rankedHandName : undefined
      const bestHandDate = allTimeBestHandWon ? allTimeBestHandWon.createdAt : undefined
      return {
        allTimeBiggestChipsWon: formatNumber(allTimeBiggestChipsWon),
        allTimeBiggestWinDate: formatDate(allTimeBiggestWinDate),
        bestHandDate: formatDate(bestHandDate),
        bestHandName,
        bestHand
      }
    },
    get showCards() {
      if (
        self.rootStore.game.isWalkthrough ||
        (self.rootStore.game.isMyFirstGame && self.rootStore.game.firstGameStage >= 4)
      ) {
        return true
      }

      if (
        self.rootStore.game.isMyPlayerTeacher &&
        self.rootStore.game.isMyPlayerObserver &&
        self.rootStore.game.isPrivateGame &&
        self.isCardsVisibleByTeacher
      ) {
        return true
      }

      return self.isCardsVisibleByEveryone || self.isCardsVisibleOnShowDown
    },
    get myPlayerLastActionText() {
      if (self.rootStore.game.isWalkthrough) return translate('newGameScreen.common.walkthrough')
      if (self.isGameOver) return translate('activeGameScreen.gameOver')
      if (self.isPreGame) return translate('newGameScreen.common.waitingForPlayers')
      if (self.isMyPlayerObserver && self.isMyPlayerTeacher) return translate('newGameScreen.player.states.teacherMode')
      if (self.isMyPlayerObserver) return translate('newGameScreen.player.states.observing')
      if (self.isGameReset) return ''
      if (self.myPlayer) return getLastActionTextForPlayer(self.myPlayer)
      return ''
    }
  }))
  .views((self) => ({
    get isTeacherObservingInPrivateGame() {
      return self.isMyPlayerTeacher && self.isMyPlayerObserver && self.isPrivateGame
    },
    get isGameSleeping() {
      return self.defaultTable?.isSleeping ?? false
    },
    get isTeacherPlayingInPrivateGame() {
      return self.isMyPlayerTeacher && !self.isMyPlayerObserver && self.isPrivateGame
    },
    get isTeacherInPrivateGame() {
      return self.isMyPlayerTeacher && self.isPrivateGame
    }
  }))
  .actions((self) => ({
    getLastActionText(player?: Player) {
      if (self.rootStore.game.isWalkthrough) return translate('newGameScreen.common.walkthrough')
      if (self.isGameOver) return translate('activeGameScreen.gameOver')
      if (self.isPreGame) return translate('newGameScreen.common.waitingForPlayers')
      if (self.isMyPlayerObserver && self.isMyPlayerTeacher) return translate('newGameScreen.player.states.teacherMode')
      if (self.isMyPlayerObserver) return translate('newGameScreen.player.states.observing')
      if (player) return getLastActionTextForPlayer(player)
      return 'PlayerHandLastAction Error'
    },
    removeRemovedPlayer(playerId: Player['id']) {
      if (!self.game) return
      self.game = {
        ...self.game,
        players: self.game?.players.filter((p: { id: Player['id'] }) => p.id !== playerId)
      }
    },
    shouldShowPreFold(): boolean {
      const showPreFold =
        !self.isMyTurn &&
        self.myPlayer &&
        !self.myPlayer.isAllIn() &&
        !self.isGamePaused &&
        !self.isGameOver &&
        !self.hasUserTappedOnAction &&
        self.shouldPrefoldButtonVisible &&
        self.myPlayer.doNoActionCount < 2 &&
        self.isPlayerInPlay(self.myPlayer)

      return !!showPreFold
    },
    setGameBegan(value: boolean) {
      self.gameBegan = value
    },
    async getNextBlinds() {
      try {
        // const currentPlayer = get(self.game, 'table[0].currentPlayerId', -1)
        // const currentPlayerData =
        // currentPlayer > -1 ? self.seatedPlayers.filter((player: any) => player.id !== currentPlayer) : []
        // const userId = currentPlayerData.length > 0 ? currentPlayerData[0].userId : ''
        const userId = self.rootStore.session.user?._id.toString() ?? null
        const channelName = self.rootStore.communication.channel?.channelName
        const me = self.players.find((player) => player.userId === userId)
        const currentPlayerId = self.defaultTable?.currentPlayerId
        // logger.log('game getNextBlinds currentPlayerId', currentPlayerId)
        // logger.log('game getNextBlinds self.defaultTable', JSON.stringify(self.defaultTable))
        // logger.log('game getNextBlinds self.game', JSON.stringify(self.game))

        if (me && userId && channelName && currentPlayerId && self.blindLevel <= 15) {
          // logger.log('game getNextBlinds for channel', channelName)
          if (channelName && userId) {
            const command: BlindCommand = {
              channelName,
              userId,
              playerId: currentPlayerId,
              blindLevel: self.blindLevel
            }
            await self.rootStore.api.mutateCalculateNextBlinds({ command })
          }
        }
      } catch (error) {
        logger.error('game getNextBlinds error', error)
      }
    },
    getPlayerActionDuration() {
      return self.defaultTable?.playerActionTimeLeft ?? self.actionTime
    },
    getTimePassedPercentage() {
      return this.getPlayerActionDuration() / self.actionTime
    },
    getPlayerRank(player_id: string) {
      for (let i = 0; i < self.playerRanks.length; i++) {
        if (self.playerRanks[i] === player_id) {
          // logger.log('###--> Fround a match')
          return self.seatedPlayers.length - i
        }
      }
      return 0
    },
    isRaiseAllInForMyPlayer(multiplier = 1) {
      if (!(self.myPlayer && self.minimumRaise)) {
        return false
      }

      return self.myPlayer.chips <= self.minimumRaise * multiplier
    }
  }))
  .actions((self) => ({
    setIsCallOptionsModalOpen(value: boolean) {
      self.isCallOptionsModalOpen = value
    },
    setIsGameStartSheetOpen(value: boolean) {
      self.isGameStartSheetOpen = value
    },
    setPotChipsPosition(value: { x: number; y: number }) {
      self.potChipsPosition = value
    },
    setIsBetModalOpen(value: boolean) {
      self.isBetModalOpen = value
    },
    setPlayerSmallVideoPosition(value: { x: number; y: number }) {
      self.playerSmallVideoPosition = value
    },
    setPreviewVideoWasShown(value: boolean) {
      self.previewVideoWasShown = value
    },
    setIsHandWinnersEvent(value: boolean) {
      self.isHandWinnersEvent = value
    },
    setIsTournamentGameOver(value: boolean) {
      self.isTournamentGameOver = value
    },
    setChipsAnimationStarted(value: boolean) {
      self.chipsAnimationStarted = value
    },
    setShouldStartFetchGameStateInterval(value: boolean) {
      self.shouldStartFetchGameStateInterval = value
    },
    setShouldCancelFetchGameStateInterval(value: boolean) {
      self.shouldCancelFetchGameStateInterval = value
    },
    setLandedGameScreenForFirstTime(value: boolean) {
      self.landedGameScreenForFirstTime = value
    },
    setShouldPlayChipsSound(value: boolean) {
      self.shouldPlayChipsSound = value
    },
    // TODO: deprecate
    setShareSheet(value: ShareSheetType | undefined) {
      self.shareSheet = value
    },
    // TODO: deprecate
    setShowShareSheet(value: boolean) {
      console.log('LOG: > setShowShareSheet > value:', value)
      // self.showShareSheet = value
    },
    setHandWinnerEventStartTime(timestamp?: number) {
      self.handWinnerEventStartTime = new Date()
    },
    setIsFirstTimeLostMoney(value: boolean) {
      self.isFirstTimeLostMoney = value
    },
    setIsWinningModalAutoClose(value: boolean) {
      self.isWinningModalAutoClose = value
    },
    setIsLeaderBoardModalAutoClose(value: boolean) {
      self.isLeaderBoardModalAutoClose = value
    },
    setiGameBackgrounded(value: boolean) {
      self.iGameBackgrounded = value
    },
    resetAvatarMap() {
      self.avatarMap = {}
    },
    setShowWinningHandLottie(value: boolean) {
      self.showWinningHandLottie = value
    },
    setTimerCountDown(value: number | undefined) {
      self.timerCountdown = value
    },
    setIsOptionModalEnabled(value: boolean) {
      self.isOptionModalEnabled = value
    },
    setIsFourColoredDeckEnabled(value: boolean) {
      self.isFourColoredDeckEnabled = value
    },
    setMyPlayerStats(stats: PlayerStatsEntityModelType) {
      self.myPlayerStats = stats
    },
    setMyPlayerGameStats(stats: PlayerGameStatsModelType) {
      self.myPlayerGameStats = stats
    },
    setPlayerUserIdForTeacher(userId: string) {
      self.playerUserIdForTeacher = userId
    },
    setIsGameAutostartEnabled(value: boolean) {
      self.isGameAutostartEnabled = value
    },
    setHasUserPreFold(value: boolean) {
      self.hasUserPreFold = value
    },
    setPauseGameTimestamp(value: number) {
      self.pauseGameTimestamp = value
    },
    setisAnyPlayerOutOfChips(value: boolean) {
      self.isAnyPlayerOutOfChips = value
    },
    setIsGameDeleted(value: boolean) {
      self.isGameDeleted = value
    },
    setGameId(value: string) {
      self.gameId = value
    },
    setCurrentBlinds(value: NextBlindType) {
      self.currentBlind = value
    },
    setNextBlinds(value: NextBlindType) {
      self.nextBlind = value
    },
    setCurrentBlindTimestamp(value: number) {
      self.currentBlindTimestamp = value
    },
    setPlayerJoinedTimeStamp(value: number) {
      self.playerJoinedTimeStamp = value
    },
    setGameResetTimestamp(value: number) {
      self.gameResetTimestamp = value
    },
    setGameKind(value: GameModeTag) {
      self.gameKind = value
    },
    setShouldHighlightCurrentPlayer(value: boolean) {
      self.shouldHighlightCurrentPlayer = value
    },
    // setStartingChips(value: number) {
    //   self.startingChips = value
    // },
    setActionTime(value: number) {
      if (value !== self.actionTime) {
        self.actionTime = value
      }
    },
    setShowPlayerActionTimer(value: boolean) {
      self.showPlayerActionTimer = value
    },

    setShowChats(value: boolean) {
      self.showChats = value
    },

    setShowConfirmLoader(value: boolean) {
      self.showConfirmLoader = value
    },
    setShowCancelLoader(value: boolean) {
      self.showCancelLoader = value
    },
    setShowPlayerTimerAfterJoinedGame(value: boolean) {
      self.showPlayerTimerAfterJoinedGame = value
    },
    // setBlindTime(value: number) {
    //   self.blindTime = value
    // },
    setHasUserDeclinedToBuy(value: boolean) {
      self.hasUserDeclinedToBuy = value
    },
    setRemainingTime(value: number) {
      self.remainingTime = value
    },
    setPauseRemainingTime(value: number) {
      self.pauseRemainingTime = value
    },
    setPauseCounter(value: number) {
      self.pauseCounter = value
    },
    setGameStartTime(value: number) {
      self.gameStartTime = value
    },
    setLeaderboardData(data: GameStatsModelType | undefined) {
      self.leaderboardData = data
    },
    setCanPlayThreeCard(data: CanPlayThreeCardDailyGameModelType | undefined) {
      self.canPlayThreeCard = data
    },
    setThreeCardResult(data: ThreeCardDailyGameStatsModelType[] | undefined) {
      self.threeCardResult?.replace(
        (data ?? []).map((hand) => ({
          ...hand,
          players: hand.players?.map((p) => ({ ...p, _id: '' }))
        }))
      )
    },
    setBlindLevel(value: number) {
      self.blindLevel = value
    },
    setIsLeaderboardAccessedFromDrawer(value: boolean) {
      self.isLeaderboardAccessedFromDrawer = value
    },
    setMyRank(value: number) {
      self.myRank = value
    },
    setIsWinnerInfoDisplayedToOutPlayer(value: boolean) {
      self.isWinnerInfoDisplayedToOutPlayer = value
    },
    setIsLeaderboardDisplayedToOutPlayer(value: boolean) {
      self.isLeaderboardDisplayedToOutPlayer = value
    },
    setHasUserTappedOnAction(value: boolean) {
      if (value) {
        self.rootStore.game.events.clearTimeoutPlayerAction()
      }
      self.hasUserTappedOnAction = value
    },
    setLastActiveCheckpoint(value: number) {
      self.lastActiveCheckpoint = value
    },
    setPlayKind(value: string) {
      self.playKind = value
    },
    setBotsCount(value: number) {
      self.botsCount = value
    },
    setIsGameAutoPaused(value: boolean) {
      self.isGameAutoPaused = value
    },
    setTableName(value: string) {
      self.tableName = value
    },
    setShowSidePots(value: boolean) {
      self.showSidePots = value
    },
    setPlayHistoryData(value: PlayHistoryModelType[]) {
      self.playHistoryData.replace(value)
    },
    setRouteBeforeCallingJoinGame(route: string | undefined) {
      self.routeBeforeCallingJoinGame = route
    }
  }))
  .actions((self) => ({
    addPlayerRanks(playerId: string) {
      if (self.playerRanks.indexOf(playerId) === -1) {
        self.playerRanks.push(playerId)
      }
    },
    addObserverPlayersNumber(playerId: number) {
      if (!self.observerPlayerNumber.includes(playerId)) {
        self.observerPlayerNumber.push(playerId)
      }
    },
    clearPlayerRanks() {
      self.playerRanks.clear()
    },
    clearObserverPlayersNumber() {
      self.observerPlayerNumber.clear()
    },
    clearMyPlayerActions() {
      if (self.rootStore.game.game?.forPlayer?.actions) {
        self.rootStore.game.game.forPlayer.actions = []
      }
    },
    resetBlind() {
      // // logger.log('resetBlindLevel')
      self.setCurrentBlinds({ big: 0, small: 0 })
      self.setNextBlinds({ big: 0, small: 0 })
      self.blindLevel = 0
    },
    setShowChatOverlay(value: boolean) {
      self.showChatOverlay = value
    },
    clearGame() {
      self.rootStore.game.closeSocketConnection()
      self.winnerCommunityCards.clear()
      self.modals.clearGameModalQueue()
      self.events.clearGameEvents()
      self.events.clearLastEventVersion()
      self.game = undefined
    },
    setAppState(nextAppState: AppStateStatus) {
      self.appState = nextAppState
    },
    setWasLocalUserVideoHidden(value: boolean) {
      self.wasLocalUserVideoHidden = value
    },
    setGame(data: GameSnapshotModelType, incomingEvent?: TableEventKind) {
      if (GameSnapshotModel.is(data)) {
        // make sure game playerActionTime is synced (after store could be reset)
        const playerActionTime = get(data, 'table[0].playerActionTime', -1)
        if (playerActionTime !== self.actionTime && incomingEvent !== TableEventKind.Reset) {
          self.setActionTime(playerActionTime)
        }
        //@ts-ignore
        const boardState = data.table[0].boardState
        //@ts-ignore
        const previousPlayerActionTimeLeft = self.game?.table[0].playerActionTimeLeft
        self.game = _.cloneDeep(data)

        if (
          incomingEvent &&
          [TableEventKind.FetchLastGameState, TableEventKind.PlayerWaitlisted].includes(incomingEvent)
        ) {
          // BAND-AID, avoid triggering player action timer to start again
          self.game.table[0].playerActionTimeLeft = previousPlayerActionTimeLeft
        }

        const sortedPlayers = orderBy(data.players, 'checkpoint', 'desc')
        // @ts-ignore
        const lastActiveCheckpoint = sortedPlayers[0] ? sortedPlayers[0].checkpoint : self.lastActiveCheckpoint
        self.setLastActiveCheckpoint(lastActiveCheckpoint)
        const gameStartTimestamp = get(self.game, 'table[0].gameStartTime', 0)
        self.setGameStartTime(gameStartTimestamp)

        if (boardState === 'predeal') {
          self.rootStore.game.setChipsAnimationStarted(false)
        }

        if (incomingEvent && [TableEventKind.HandCurrentPlayer].includes(incomingEvent)) {
          self.setHasUserTappedOnAction(false)
          self.rootStore.game.setChipsAnimationStarted(false)
          self.rootStore.game.setPlayerActionTimerExpired(false)
          // Let's start the timer for getting last game state here
          self.setShouldStartFetchGameStateInterval(true)
        } else if (incomingEvent === TableEventKind.StreetFinished) {
          self.setHasUserTappedOnAction(true)

          if (['deal', 'flop', 'turn', 'river'].includes(boardState)) {
            setTimeout(() => {
              self.rootStore.game.setChipsAnimationStarted(true)
            }, 500)
          }
        }

        // Cancel the timer if any event except HandCurrentPlayer comes in.
        if (incomingEvent && incomingEvent !== TableEventKind.HandCurrentPlayer) {
          self.setShouldCancelFetchGameStateInterval(true)
        }

        const nextBlindValues = get(self.game, 'table[0].nextBlinds', { big: 0, small: 0 })
        const currentBlindValues = get(self.game, 'table[0].blinds', { big: 0, small: 0 })

        if (self.currentBlind.big < currentBlindValues.big && self.currentBlind.small < currentBlindValues.small) {
          self.setCurrentBlinds(currentBlindValues.small === null ? { big: 0, small: 0 } : currentBlindValues)
        }
        if (self.nextBlind.big < nextBlindValues.big && self.nextBlind.small < nextBlindValues.small) {
          self.setNextBlinds(nextBlindValues.small === null ? { big: 0, small: 0 } : nextBlindValues)
        }
      } else {
        MappingError.check(GameSnapshotModel.name, GameSnapshotModel, data)
      }
    },
    //TODO: Find the type and replace any
    convertCardsToReadable(cards: string[]) {
      // Our return array
      const convertedCards: string[][] = []
      // For each card we make its value and suit readable and add it to convertedCards array
      cards?.forEach((card: string) => {
        let readableVal = ''
        let readableSuit = ''

        const val = card[0]
        switch (val) {
          case '2':
            readableVal = 'Two'
            break
          case '3':
            readableVal = 'Three'
            break
          case '4':
            readableVal = 'Four'
            break
          case '5':
            readableVal = 'Five'
            break
          case '6':
            readableVal = 'Six'
            break
          case '7':
            readableVal = 'Seven'
            break
          case '8':
            readableVal = 'Eight'
            break
          case '9':
            readableVal = 'Nine'
            break
          case 'T':
            readableVal = 'Ten'
            break
          case 'J':
            readableVal = 'Jack'
            break
          case 'Q':
            readableVal = 'Queen'
            break
          case 'K':
            readableVal = 'King'
            break
          case 'A':
            readableVal = 'Ace'
            break
          default:
            readableVal = ''
        }

        const suit = card[1]
        switch (suit) {
          case 's':
            readableSuit = 'Spades'
            break
          case 'h':
            readableSuit = 'Hearts'
            break
          case 'd':
            readableSuit = 'Diamonds'
            break
          case 'c':
            readableSuit = 'Clubs'
            break
          default:
            readableSuit = ''
        }

        convertedCards.push([readableVal, readableSuit])
      })
      return convertedCards
    },
    // unshiftGameWinner() {
    //   self.winners.unshift()
    // },
    getObserverPlayerNumber() {
      return self.observerPlayerNumber
    },
    setGameAllIn(data: GameSnapshotModelType) {
      // // logger.log('setGameAllIn', GameSnapshotModel.is(data), data)
      const playerUserId = get(data, 'snapshot.forPlayer.userId', '')
      const userId = self.rootStore.session.user?._id
      const isMe = playerUserId === userId
      const firstName = get(data, 'snapshot.forPlayer.name', 'PLAYER')
      self.allIn = isMe ? 'YOU ARE ALL IN!' : `${firstName} IS ALL IN!`
      // self.rootStore.session.createAllInModal()
    },
    clearAllIn() {
      self.allIn = ''
    },
    setCreateGameInfo(channel?: ChannelModelType, game?: GameEntityModelType) {
      if (!channel) {
        throw new Error('Channel entity missing')
      }
      if (!ChannelModel.is(channel)) {
        MappingError.check(ChannelModel.name, ChannelModel, channel)
        throw new Error('Channel entity is wrong')
      }

      if (!game) {
        throw new Error('Channel entity missing')
        // } else if (!GameModel.is(game)) {
        // TODO: Why is this failing? Seems a mst issue rather than a data structure issue
        //   MappingError.check(GameModel.name, GameModel, game)
        //   throw new Error('Game entity is wrong')
      }
      if (!game.hash) {
        throw new Error('Game does not have hash')
      }

      self.rootStore.share.setShare(game.hash)
      // self.rootStore.communication.setChannel(ChannelModel.create({ ...channel }))
      self.rootStore.communication.setChannel({ ...channel })
      self.rootStore.game.setGameId(game._id)
    }
  }))
  .actions((self) => ({
    async makeNewGame(gameMode: GameModeTag, gameName?: string) {
      try {
        self.clearGame()
        const result = await self.rootStore.api.mutateMakeNewGame(
          {
            gameMode,
            gameName
          },
          (qb) =>
            qb
              // mst-gql just generates automatically the primitives fields, not child fields
              // we need to specify the child fields ourselves.
              .channel(channelModelPrimitives)
              .game(gameEntityModelPrimitives.snapshot(gameSnapshotModelPrimitives))
        )

        console.log('LOG: > makeNewGame > result.makeNewGame:', result.makeNewGame)
        // TODO: Type better
        const channel = result.makeNewGame.channel as ChannelModelType | undefined
        const game = result.makeNewGame.game as GameEntityModelType | undefined

        self.setCreateGameInfo(channel, game)
      } catch (error) {
        logger.error('makeNewGame', error)
        throw error
      }
    },
    async fetchLastGameState() {
      const channelName = self.rootStore.communication.channel?.channelName
      const userId = self.rootStore.session.user?._id.toString()
      const playerName = self.rootStore.session.user?.name

      if (channelName && userId && playerName) {
        const response = await self.rootStore.api.mutateFetchLastGameState(
          {
            command: {
              channelName,
              userId,
              playerName
            }
          },
          (qb) => gameEntityModelPrimitives.snapshot(gameSnapshotModelPrimitives).tournament(tournamentModelPrimitives)
        )

        if (!response.fetchLastGameState) {
          setTimeout(() => {
            // This timeout will present the UI and we can go back to home screen
            self.rootStore.game.createGameDeletedAlert()
          }, 500)
          return
        }

        if (!response.fetchLastGameState.snapshot) {
          throw new Error('FetchLastGameState error: Missing snapshot')
        }

        // TODO: This is breaking but it should be validated.
        // const validatedGameSnapshot = GameSnapshotModel.create(response.fetchLastGameState.snapshot)
        // self.rootStore.game.setGame(
        //   response.fetchLastGameState.snapshot as GameSnapshotModelType,
        //   TableEventKind.FetchLastGameState
        // )
      }
    },
    async addBots(botCount: number) {
      const channelName = self.rootStore.communication.channel?.channelName
      const userId = self.rootStore.session.user?._id.toString()

      if (channelName && userId) {
        await self.rootStore.api.mutateAddBots({
          command: {
            channelName,
            botCount,
            userId,
            startingChips: self.rootStore.game.startingChips
          }
        })
      }
    },
    async joinGame() {
      // logger.info('[game] joinGame shouldSyncGameState', shouldSyncGameState)
      const channelName = self.rootStore.communication.channel?.channelName
      const userId = self.rootStore.session.user?._id.toString()
      const playerName = self.rootStore.session.user?.name
      const playerKind = self.rootStore.game.playKind

      //TODO: this is hiding the start bottom sheel modal after selecting an option on av modal
      //self.rootStore.messages.clearSnack()

      if (channelName && userId && playerName) {
        try {
          const response = await self.rootStore.api.mutateJoinGame(
            {
              command: {
                channelName,
                userId,
                playerName,
                startingChips: self.rootStore.game.startingChips,
                playKind: playerKind === 'observer' ? PlayKind.observe : PlayKind.play,
                playerKind: PlayerKind.HUMAN
              }
            },
            (qb) =>
              gameEntityModelPrimitives.snapshot(gameSnapshotModelPrimitives).tournament(tournamentModelPrimitives)
          )

          if (!response.joinGame) {
            self.rootStore.game.createGameDeletedAlert()
            return
          }

          if ([GameModeTag.practice, GameModeTag.practiceTournament, GameModeTag.threeCard].includes(self.gameKind)) {
            if (!self.botsAlreadyJoined) {
              if (self.isThreeCard) {
                const botKind = BOT_KINDS[0]
                const botUserId1 = nanoid().split('-').join('').slice(0, 24)
                const botUserId2 = nanoid().split('-').join('').slice(0, 24)
                const [botName1, botName2] = THREE_CARD_BOT_NAMES

                await self.rootStore.api.mutateJoinGame({
                  command: {
                    channelName,
                    userId: botUserId1,
                    playerName: botName1,
                    startingChips: self.rootStore.game.startingChips,
                    playKind: PlayKind.play,
                    playerKind: PlayerKind.BOT,
                    botKind: botKind as BotKind
                  }
                })
                await self.rootStore.api.mutateJoinGame({
                  command: {
                    channelName,
                    userId: botUserId2,
                    playerName: botName2,
                    startingChips: self.rootStore.game.startingChips,
                    playKind: PlayKind.play,
                    playerKind: PlayerKind.BOT,
                    botKind: botKind as BotKind
                  }
                })
              } else {
                await Promise.allSettled(
                  Array.from({ length: self.botsCount }).map((_, index) => {
                    const botUserId = nanoid().split('-').join('').slice(0, 24)
                    // const botKind = BOT_KINDS[Math.floor(Math.random() * BOT_KINDS.length)]
                    // TODO: change when more bots are implemented
                    const botKind = 'BOT_PP'
                    const botName = self.isThreeCard ? THREE_CARD_BOT_NAMES[index] : `\u{1F916} Bot ${index + 1}`
                    return self.rootStore.api.mutateJoinGame({
                      command: {
                        channelName,
                        userId: botUserId,
                        playerName: botName,
                        startingChips: self.rootStore.game.startingChips,
                        playKind: PlayKind.play,
                        playerKind: PlayerKind.BOT,
                        botKind: botKind as BotKind
                      }
                    })
                  })
                )
              }
            }
          }

          if (response.joinGame.hash) {
            self.rootStore.share.setShare(response.joinGame.hash)
          } else {
            logger.error('Game is missing hash')
          }

          // TODO this setGameKind should be removed
          if (response.joinGame.kind) {
            const kind = response.joinGame.kind as GameModeTag
            self.setGameKind(kind)
          }
          self.setTableName(response.joinGame.name ?? '')
          self.setGameId(response.joinGame._id)

          const joinGameSnapshot = response.joinGame.snapshot

          self.rootStore.game.setIsHandWinnersEvent(false)

          if (joinGameSnapshot) {
            self.rootStore.game.setGame(joinGameSnapshot as GameSnapshotModelType, TableEventKind.HandCurrentPlayer)
            const startTimerCheckpoint = joinGameSnapshot.table[0].startTimerCheckpoint
            const secondsPassed = GAME_CONFIG.START_TIMER_DELAY - Math.floor((Date.now() - startTimerCheckpoint) / 1000)

            if (
              self.rootStore.game.seatedPlayers.length >= 2 &&
              self.rootStore.game.isGameReadyToPlay &&
              secondsPassed > 0 &&
              self.rootStore.game.gameKind !== GameModeTag.threeCard
            ) {
              self.rootStore.game.setTimerCountDown(Math.min(GAME_CONFIG.START_TIMER_DELAY, secondsPassed))
            }

            if (self.rootStore.game.isGameOver) {
              return { redirectToGameOver: true }
            }
            return { redirectToGameOver: false }
          }
          return { redirectToGameOver: false }
        } catch (error) {
          logger.error('[game] joinGame', error)
          const statusCode = get(error, 'response.errors[0].extensions.response.statusCode', 400)
          // TODO: review this
          // @ts-ignore
          if (statusCode === 404) {
            self.rootStore.game.createGameDeletedAlert()
          }
          // self.rootStore.game.clearGameModalQueue()
          // RootNavigation.navigate(DrawerRoutes.lobby)
          throw error
        }
      }
    },
    getplayerTotalChipCount(chipvalue: number, betvalue: number) {
      if (chipvalue && betvalue) {
        return chipvalue + betvalue
      }
      if (chipvalue) {
        return chipvalue
      }
      if (betvalue) {
        return betvalue
      }
      return -1
    },
    async doAllInForPlayer() {
      try {
        self.setHasUserTappedOnAction(true)
        const userId = self.rootStore.session.user?._id.toString()
        const channelName = self.rootStore.communication.channel?.channelName
        if (self.myPlayer) {
          const playerId = self.myPlayer.id
          // logger.log('game doAllInForPlayer for channel', channelName)
          if (channelName && userId) {
            const command: TableCommand = {
              channelName,
              playerId,
              userId
            }
            await self.rootStore.api.mutateDoAllInForPlayer({ command })
          }
        }
      } catch (error) {
        logger.error('game doAllInForPlayer error', error)
        self.setHasUserTappedOnAction(false)
      }
    },
    async doBetForPlayer(amount: number, kind: BetKind) {
      try {
        self.setHasUserTappedOnAction(true)
        const userId = self.rootStore.session.user?._id.toString()
        const channelName = self.rootStore.communication.channel?.channelName
        const me = self.players.find((player) => player.userId === userId)
        // logger.log('game doBetForPlayer', channelName, userId, self.players.length, me)
        if (me) {
          const playerId = me.id
          // logger.log('game doBetForPlayer for channel', channelName)
          if (channelName && userId) {
            const command: BetCommand = {
              channelName,
              bet: {
                amount,
                kind
              },
              playerId,
              userId
            }
            await self.rootStore.api.mutateDoBetForPlayer({ command })
            // const response = await self.rootStore.api.mutateDoBetForPlayer({ command })
            // // logger.log('game doBetForPlayer', response.doBetForPlayer)
            // self.setGame(response.doBetForPlayer.snapshot)
            // if (response.doBetForPlayer?.messages) {
            //   self.rootStore.game.events.pushGameEvents(response.doBetForPlayer?.messages)
            // }
            // const mutate = self.rootStore.api.mutateDoBetForPlayer({ command })
            // const env = getEnv<RootStoreEnv>(self)
            // const websocketClient = env.gqlWsClient
            // const { query, variables } = mutate
            // const response = await websocketClient.send({
            //   operationName: null,
            //   query,
            //   variables,
            // })
            // // logger.log('game doBetForPlayer', response)
            // const chatBetType =
            //   command.bet.kind === BetKind.BET
            //     ? translate('chatDealerMessages.playerBet')
            //     : translate('chatDealerMessages.playerRaised')
            // self.rootStore.communication.sendChat(
            //   `${self.rootStore.session.user?.name} ${chatBetType} ${amount}.`,
            //   ChatKind.dealer,
            //   'white'
            // )
          }
        }
      } catch (error) {
        logger.error('game doBetForPlayer error', error)
        self.rootStore.game.setHasUserTappedOnAction(false)
        throw error
      } finally {
        // We add a timeout so buttons no longer flash after we submit our player action.
        // setTimeout(() => {
        //   self.setHasUserTappedOnAction(false)
        // }, 250)
      }
    },
    async doBuyForPlayer(amount: number, userId?: string, givenByTeacher?: boolean) {
      try {
        if (!userId) {
          userId = self.rootStore.session.user?._id.toString()
        }
        const channelName = self.rootStore.communication.channel?.channelName
        const player = self.players.find((player) => player.userId === userId)
        logger.log('game doBuyForPlayer', channelName, userId, self.players.length, player)
        if (player) {
          const playerId = player.id
          logger.log('game doBuyForPlayer for channel', channelName)
          if (channelName && userId) {
            const command: BuyCommand = {
              channelName,
              amount,
              playerId,
              userId,
              givenByTeacher
            }
            await self.rootStore.api.mutateDoBuyForPlayer({ command })
            // logger.log('game doBuyForPlayer', response)
          }
        }
      } catch (error) {
        logger.error('game doBuyForPlayer error', error)
        self.setHasUserTappedOnAction(false)
      } finally {
        // We add a timeout so buttons no longer flash after we submit our player action.
        // setTimeout(() => {
        //   self.setHasUserTappedOnAction(false)
        // }, 250)
      }
    },
    async doBuyForAllPlayers(amount: number) {
      try {
        const channelName = self.rootStore.communication.channel?.channelName
        const userId = self.rootStore.session.user?._id.toString()
        if (!userId) {
          throw Error(`Invalid userId ${userId}`)
        }
        if (!channelName) {
          throw Error(`Invalid channelName ${channelName}`)
        }
        await self.rootStore.api.mutateDoBuyForAllPlayers({ command: { amount, channelName, userId: userId } })
      } catch (error) {
        logger.error('game doBuyForAllPlayers error', error)
      }
    },
    async doCallForPlayer() {
      // logger.log('[game] doCallForPlayer')
      try {
        self.setHasUserTappedOnAction(true)
        const userId = self.rootStore.session.user?._id.toString()

        const channelName = self.rootStore.communication.channel?.channelName
        if (self.myPlayer) {
          const playerId = self.myPlayer.id
          if (channelName && userId) {
            const command: TableCommand = {
              channelName,
              playerId,
              userId
            }

            await self.rootStore.api.mutateDoCallForPlayer({ command })
          }
        }
      } catch (error) {
        logger.error('game doCallForPlayer error', error)
        self.setHasUserTappedOnAction(false)
      }
    },
    async doCheckForPlayer() {
      try {
        self.setHasUserTappedOnAction(true)
        const userId = self.rootStore.session.user?._id.toString()
        const channelName = self.rootStore.communication.channel?.channelName
        if (self.myPlayer) {
          const playerId = self.myPlayer.id
          // logger.log('game doCheckForPlayer for channel', channelName)
          if (channelName && userId) {
            const command: TableCommand = {
              channelName,
              playerId,
              userId
            }
            // @TODO: convert to WebSocket.send
            await self.rootStore.api.mutateDoCheckForPlayer({ command })
          }
        }
      } catch (error) {
        logger.error('game doCheckForPlayer error', error)
        self.setHasUserTappedOnAction(false)
      }
    },
    async doFoldForPlayer() {
      try {
        self.setHasUserTappedOnAction(true)
        const userId = self.rootStore.session.user?._id.toString()
        const channelName = self.rootStore.communication.channel?.channelName
        if (self.myPlayer) {
          const playerId = self.myPlayer.id
          // logger.log('game doFoldForPlayer for channel', channelName)

          if (channelName && userId) {
            const command = {
              channelName,
              playerId,
              userId
            }
            // @TODO: implement fold proxy
            // if (self.rootStore.environment.isLocal) {
            await self.rootStore.api.mutateDoFoldForPlayer({ command })
          }
        }
      } catch (error) {
        logger.error('game doFoldForPlayer error', error)
        self.setHasUserTappedOnAction(false)
      }
    },
    async setPlayerBuyIn() {
      try {
        self.setHasUserTappedOnAction(true)
        const userId = self.rootStore.session.user?._id.toString()
        const channelName = self.rootStore.communication.channel?.channelName
        if (self.myPlayer) {
          const playerId = self.myPlayer.id
          // logger.log('game setPlayerBuyIn for channel', channelName)
          if (channelName && userId) {
            const command: TableCommand = {
              channelName,
              playerId,
              userId
            }
            await self.rootStore.api.mutateSetPlayerBuyIn({ command })
            // logger.log('game setPlayerBuyIn', JSON.stringify(response.setPlayerBuyIn))
          }
        }
      } catch (error) {
        logger.error('game setPlayerBuyIn error', error)
        self.setHasUserTappedOnAction(false)
      }
    },
    async wakeUp() {
      try {
        // // logger.log('session-game afterCreate wake the up lambdas')
        await self.rootStore.api.queryHello()
        // // logger.log('session-game afterCreate queryHello', response)
      } catch (error) {
        logger.error('session-game afterCreate', error)
      }
    },
    teardown() {
      logger.info('[session-game] teardown')
      self.rootStore.game.setLeaderboardData(undefined)
      self.rootStore.game.clearGameSheetQueue()
      self.rootStore.game.setShowPlayerActionTimer(true)
      self.rootStore.game.setShowPlayerTimerAfterJoinedGame(false)
      self.rootStore.communication.setIsTyping(false)
      self.rootStore.communication.setTypingUser('')
      self.rootStore.game.setShowChatOverlay(false)
      self.rootStore.game.setGameBegan(false)
      self.rootStore.game.setShouldHighlightCurrentPlayer(false)
      self.setHasUserTappedOnAction(false)
      self.rootStore.game.clearObserverPlayersNumber()
      self.rootStore.game.clearAllIn()
      self.rootStore.communication.clearChannel()
      self.rootStore.game.clearGame()
      self.rootStore.game.clearCurrentTournament()
      self.rootStore.game.events.clearGameEvents()
      self.rootStore.game.clearGameModalQueue()
      self.rootStore.game.setHasUserDeclinedToBuy(false)
      // self.rootStore.game.clearGameWinners()
      self.rootStore.messages.clearMessages()
      self.rootStore.communication.clearChannelChats()
      self.rootStore.communication.setUsersOnVideoCall([])
      self.rootStore.share.clearShare()
      self.rootStore.game.resetTimer()
      self.rootStore.game.resetBlind()
      //NOTE: Default time for quick play is 60sec and tournament is 20 sec
      self.rootStore.game.setActionTime(defaultActionTime)
      self.rootStore.game.setIsWinnerInfoDisplayedToOutPlayer(false)
      self.rootStore.game.clearPlayerRanks()
      self.rootStore.game.hideCards()
      self.rootStore.game.setPlayKind('')
      self.rootStore.game.setBotsCount(0)
      // // logger.log('game teardown()')
      // TODO FRANCO: is it necesary to set gameKind here? is being setted when user creates a game
      self.rootStore.game.setGameKind(GameModeTag.default)
      self.rootStore.game.setPlayerJoinedTimeStamp(0)
      self.rootStore.communication.endCall()
      self.rootStore.game.setIsLeaderboardDisplayedToOutPlayer(false)
      self.rootStore.game.setHasUserPreFold(false)
      self.rootStore.game.setIsGameDeleted(false)
      if (!self.rootStore.network.isNetworkDown) self.rootStore.messages.clearSnack()
      self.rootStore.game.setGameResetTimestamp(0)
      self.rootStore.game.setPauseGameTimestamp(0)
      self.rootStore.communication.clearCelebrations()
      self.rootStore.communication.clearShowCards()
      self.setShouldStartFetchGameStateInterval(false)
      self.setShouldCancelFetchGameStateInterval(false)
      self.rootStore.game.setPlayerActionTimerExpired(false)
      // self.rootStore.game.setTableName('')
      self.rootStore.telemetry.clearUserGameId(self.rootStore.session.user)
      // tournament
      self.rootStore.game.setIsFirstTime(false)
      self.rootStore.communication.setPlayerVideoCallStatus(false, false, false)
      self.rootStore.game.setTimerCountDown(undefined)
      //landed game screen
      self.rootStore.game.setLandedGameScreenForFirstTime(true)
      self.rootStore.share.setIsShareOpen(false)
      self.rootStore.game.setPreviewVideoWasShown(false)
      self.rootStore.game.setIsHandWinnersEvent(false)
      self.rootStore.game.setIsTournamentGameOver(false)
      self.rootStore.communication.setUserTappedOnGoLive(false)
      self.rootStore.game.setIsBetModalOpen(false)
      self.rootStore.game.setIsCallOptionsModalOpen(false)
      self.rootStore.game.setIsGameStartSheetOpen(false)
      self.rootStore.game.setToast(null)

      self.rootStore.game.isMyFirstGame = false
      self.rootStore.game.firstGameStage = 0
    },
    closeSocketConnection(closeReason = 'Game Ended') {
      try {
        const env = getEnv<RootStoreEnv>(self)
        const webSocketClient = env.gqlWsClient || env.graphqlWsClient
        logger.debug('doLeaveGame force socket to close...')
        if (Platform.OS === 'web') {
          self.rootStore?.network.setWebSocketState(WebSocketConnectionState.closed)
        }
        // unsubsribe all GraphQL Subscriptions:
        self.rootStore.messages.unsubscribeAll()
        // close client socket
        webSocketClient.terminate(closeReason)
        //@ts-ignore
        self.rootStore.api.setHttpClient(env.gqlHttpClient)
        logger.debug('doLeaveGame force socket to closed')
      } catch (error) {
        logger.error('Error in doLeaveGame socket.close', error)
      }
    },
    async leaveCurrentGame(callApi = true) {
      const userId = self.rootStore.session.user?._id.toString()
      const channelName = self.rootStore.communication.channel?.channelName
      const playerId = self.myPlayer?.id
      const isTeacher = self.rootStore.session.user?.userRoles?.includes('teacher')

      let leftTheGameSucessfully = true

      try {
        if (channelName && userId) {
          // Game Leave event moved to server side
          if (callApi && playerId) {
            try {
              self.rootStore.communication.setPlayerVideoCallStatus(false, false, false)
              await self.rootStore.api.mutateLeaveChannel({ channelName, userId })
            } catch (error) {
              logger.error('doLeaveGame mutateLeaveChannel', error)
              leftTheGameSucessfully = false
            }
            try {
              const response = await self.rootStore.api.mutateDoLeaveGameForPlayer({
                command: {
                  channelName,
                  userId,
                  playerId
                }
              })

              if (!response.doLeaveGameForPlayer) {
                self.rootStore.game.teardown()
                self.rootStore.game.createGameDeletedAlert()
                return
              }
            } catch (error) {
              logger.error('doLeaveGame mutateDoLeaveGameForPlayer', error)
              const statusCode = get(error, 'response.errors[0].extensions.response.statusCode', 400)
              // @ts-ignore
              if (statusCode === 404) {
                self.rootStore.game.createGameDeletedAlert()
              }
              leftTheGameSucessfully = false
              throw error
            }
          }

          if (leftTheGameSucessfully) {
            const isAnotherTeacherInGame = self.rootStore.game.players.find(
              (player) => player?.userRoles?.includes('teacher') && player.userId !== userId
            )
            if (!isAnotherTeacherInGame) {
              // This user is the only teacher in game, if is paused, resume the game
              if (isTeacher && self.rootStore.game.isGamePaused) {
                await self.rootStore.game.resumeTheGame()
              }
            }
            self.rootStore.game.teardown()
          }
        }
      } finally {
        // Safeguard: if everything fails at least clear the store
        self.rootStore.game.teardown()
      }
    },
    async removePlayerFromGame() {
      try {
        const channelName = self.rootStore.communication.channel?.channelName
        const userId = self.playerUserIdForTeacher
        const user = self.seatedPlayers.filter((player) => player.userId === userId)
        const playerId = user[0].id
        if (channelName && playerId) {
          const response = await self.rootStore.api.mutateRemovedPlayerByTeacher({
            command: {
              channelName,
              userId,
              playerId
            }
          })
          //Todo: Check the attributes and update
          //@ts-ignore
          self.rootStore.game.setGame(response.removedPlayerByTeacher.snapshot)
          self.setPlayerUserIdForTeacher('')
          // logger.log('Remove Player: ', response)
        }
      } catch (error) {
        logger.error('Error in removing player from Game', error)
      }
    },
    async unseatPlayer() {
      const channelName = self.rootStore.communication.channel?.channelName
      const userId = self.playerUserIdForTeacher
      const playerData = self.seatedPlayers.filter((player) => player.userId === userId)
      if (playerData) {
        const playerId = playerData[0].id
        // logger.log('unseatPlayer', channelName, playerId, userId)
        if (channelName && userId) {
          try {
            await self.rootStore.api.mutateUnseatPlayerByTeacher({
              command: {
                channelName,
                userId,
                playerId
              }
            })
            self.setPlayerUserIdForTeacher('')
            // logger.log('unseatPlayer Success response: ', response)
          } catch (error) {
            logger.error('unseatPlayer API Error: ', error)
            throw error
          }
        }
      }
    },
    async seatBackPlayerInGame(playerUserId?: string) {
      const channelName = self.rootStore.communication.channel?.channelName
      const userId = playerUserId
        ? playerUserId
        : self.playerUserIdForTeacher === ''
          ? self.rootStore.session.user?._id.toString()
          : self.playerUserIdForTeacher
      const playerData = self.seatedPlayers.filter((player) => player.userId === userId)
      if (playerData) {
        const playerId = playerData[0].id
        // logger.log('SeatBack Player', channelName, playerId, userId)
        if (channelName && userId) {
          try {
            await self.rootStore.api.mutateSeatedPlayerByTeacher({
              command: {
                channelName,
                userId,
                playerId
              }
            })
            self.setPlayerUserIdForTeacher('')
            // logger.log('SeatBack Player Success response: ', response)
          } catch (error) {
            logger.error('SeatBack Player API Error: ', error)
            throw error
          }
        }
      }
    },
    checkRoyalFlushCards(royalCards: string[], sevenWinnerCommunityCards: string[]) {
      return royalCards.every((element: string) => {
        if (sevenWinnerCommunityCards.includes(element)) {
          return true
        }
        return false
      })
    }
  }))
  .actions((self) => ({
    async pauseTheGame() {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        const channelName = self.rootStore.communication.channel?.channelName
        if (self.myPlayer) {
          if (channelName && userId) {
            await self.rootStore.api.mutatePauseGame({
              command: {
                channelName,
                userId
              }
            })
          }
        }
      } catch (error) {
        logger.error('[session-game] pauseTheGame', error)
        throw error
      }
    },
    async resumeTheGame() {
      try {
        const userId = self.rootStore.session.user?._id.toString()
        const channelName = self.rootStore.communication.channel?.channelName
        if (self.myPlayer) {
          if (channelName && userId) {
            try {
              await self.rootStore.api.mutateResumeGame({
                command: {
                  channelName,
                  userId
                }
              })
            } catch (error) {
              logger.error('[game] resumeTheGame', error)
              throw error
            }
          }
        }
      } catch (error) {
        logger.error('Resume Game API Error: ', error)
        throw error
      }
    },
    async startGame() {
      const userId = self.rootStore.session.user?._id.toString()
      const channelName = self.rootStore.communication.channel?.channelName
      try {
        if (channelName && userId) {
          self.rootStore.messages.clearSnack()
          self.rootStore.api.mutateStartGame({
            command: {
              channelName,
              userId,
              enableAutoStart: false,
              options: {
                blindTimerCountdown: self.rootStore.game.blindTime / 60,
                mode: self.gameKind
              }
            }
          })
        }
      } catch (error) {
        logger.error('startGame Error', error)
        throw error
      }
    },
    async createTournament(
      tournamentName: string,
      gameMode: GameModeTag,
      blindTimer?: number,
      actionTime?: number,
      startingChips?: number,
      difficulty?: TournamentDifficulty,
      tournamentAccessControl?: TournamentAccessControl
    ) {
      const payload = {
        name: tournamentName,
        gameMode,
        startTime: new Date(),
        endTime: new Date(),
        blindTimer: blindTimer ? blindTimer : 2,
        startingChips: startingChips ? startingChips : 10000,
        actionTime: actionTime ? actionTime : defaultPlayerActionTimeTournament, //time needed in sec
        difficulty,
        accessControl: tournamentAccessControl
      }
      try {
        const result = await self.rootStore.api.mutateCreateTournament(payload, (qb) =>
          qb
            // mst-gql just generates automatically the primitives fields, not child fields
            // we need to specify the child fields ourselves.
            .channel(channelModelPrimitives)
            .game(gameEntityModelPrimitives.snapshot(gameSnapshotModelPrimitives).tournament(tournamentModelPrimitives))
        )

        if (!result.createTournament.channel) {
          throw new Error('Missing Channel info')
        }
        if (!result.createTournament.game) {
          throw new Error('Missing Game info')
        }

        const channel = ChannelModel.create({ ...result.createTournament.channel })
        // TODO: This is failing. No idea why. Casting as GameEntityModelType | undefined as a workaround
        // const game = GameEntityModel.create({ ...result.createTournament.game })
        const game = result.createTournament.game as GameEntityModelType | undefined

        if (!game?.tournament) {
          throw new Error('Missing Tournament info')
        }

        self.setCreateGameInfo(channel, game)
        self.rootStore.game.clearCurrentTournament()
        self.rootStore.game.setCurrentTournament(game.tournament as TournamentModelType)
      } catch (error) {
        logger.log(`Error in creating tournament: ${error}`)
        throw error
      }
    },
    async updateGame(autostart: boolean) {
      try {
        const channelName = self.rootStore.communication.channel?.channelName
        if (channelName) {
          await self.rootStore.api.mutateUpdateGame({ autostart, channelName })
          self.setIsGameAutostartEnabled(autostart)
        }
      } catch (error) {
        // logger.log('Update Game autostart error', error)
      }
    },
    async createUserGameSchedule(
      tournamentName: string,
      gameMode: GameModeTag,
      blindTimer?: number,
      actionTime?: number,
      startingChips?: number,
      difficulty?: TournamentDifficulty,
      tournamentAccessControl?: TournamentAccessControl,
      scheduleForLaterAt?: Date
    ): Promise<UserGameScheduleResponseModelType> {
      try {
        const now = new Date()
        now.setMinutes(now.getMinutes() + 15)

        const payload = {
          name: tournamentName,
          //gameMode,
          scheduleTime: scheduleForLaterAt ?? now,
          blindTimer: blindTimer ? blindTimer : 2,
          startingChips: startingChips ? startingChips : 10000,
          actionTime: actionTime ? actionTime : defaultPlayerActionTimeTournament //time needed in sec
          //difficulty,
          //accessControl: tournamentAccessControl
        }

        const result = await self.rootStore.api.mutateCreateUserGameSchedule({
          inputCreateUserGameSchedule: payload
        })

        self.rootStore.game.setUserGameScheduleFullResponse(result.createUserGameSchedule)
        return result.createUserGameSchedule
      } catch (e) {
        console.log(e)
        throw e
      }
    },
    async deleteUserGameSchedule(hash: string) {
      try {
        await self.rootStore.api.mutateDeleteUserGameSchedule({
          hash
        })

        self.rootStore.game.setUserGameScheduleFullResponse(null)
      } catch (e) {
        console.log(e)
        throw e
      }
    },

    async queryUserGameScheduleByHash(hash: string): Promise<UserGameScheduleResponseModelType> {
      try {
        const result = await self.rootStore.api.queryQueryUserGameScheduleByHash(
          {
            userGameScheduledHash: hash
          },
          undefined,
          { fetchPolicy: 'no-cache' }
        )

        const userGameScheduleResult = result.queryUserGameScheduleByHash

        self.rootStore.game.setUserGameScheduleFullResponse(userGameScheduleResult)
        return userGameScheduleResult
      } catch (e) {
        console.log(e)
        throw e
      }
    },
    async handleAppStateChangeForGame(nextAppState: AppStateStatus) {
      try {
        if (nextAppState === 'background' || nextAppState === 'inactive') {
          self.rootStore.session.updateAppState('backgrounded')
          await self.rootStore.messages.unsubscribeGraphQLSubscription(true)
        }
        if (nextAppState === 'background') {
          const { isLocalUserVideoHidden, isLocalUserJoined } = self.rootStore.communication
          self.setWasLocalUserVideoHidden(isLocalUserVideoHidden)
          if (isLocalUserJoined) {
            self.rootStore.communication.hideVideoAndMuteCallLocalUser()
          }
          self.rootStore.messages.clearMessages()
          self.rootStore.game.events.clearGameEvents()
          self.rootStore.game.clearGameModalQueue()
        }

        if (self.appState !== 'active' && nextAppState === 'active') {
          self.rootStore.game.setPlayerActionTimerExpired(false)
          self.rootStore.session.updateAppState('foregrounded')
          // logger.log('session-game app has come to the foreground!')
          try {
            // reset local state and sync since user was in the background
            self.rootStore.game.events.clearTimeout()
            self.rootStore.messages.clearMessages()
            self.rootStore.game.setTimerCountDown(undefined)
            self.rootStore.game.events.clearGameEvents()
            self.rootStore.game.clearGameModalQueue()
            self.rootStore.game.setHasUserTappedOnAction(false)
            self.rootStore.game.setShowPlayerTimerAfterJoinedGame(false)
            // refresh game state from server
            self.rootStore.permissions.refresh()
          } catch (error) {
            console.log('LOG: handleAppStateChangeForGame > error:', error)
            logger.error('session-game AppState handleAppStateChangeForGame error', error)
            const statusCode = get(error, 'response.errors[0].extensions.response.statusCode', 400)
            // TODO: fix
            // @ts-ignore
            if (statusCode === 404) {
              self.rootStore.game.createGameDeletedAlert()
            }
          }
        }
        // // logger.log('session-game AppState changed', self.appState)
      } catch (error) {
        logger.error('session-game', error)
      } finally {
        self.setAppState(nextAppState)
      }
    },
    async showCardsForTeacher() {
      const userId = self.rootStore.session.user?._id.toString()
      const channelName = self.rootStore.communication.channel?.channelName
      if (channelName && userId) {
        try {
          await self.rootStore.api.mutateShowCardsForTeacher({
            command: {
              channelName,
              userId
            }
          })
        } catch (error) {
          logger.error('showCardsForTeacher API Error: ', error)
          throw error
        }
      }
      // }
    },
    async showCardsForEveryone() {
      const userId = self.rootStore.session.user?._id.toString()
      const channelName = self.rootStore.communication.channel?.channelName
      if (channelName && userId) {
        try {
          await self.rootStore.api.mutateShowCardsForEveryone({
            command: {
              channelName,
              userId
            }
          })
        } catch (error) {
          logger.error('showCardsForEveryone API Error: ', error)
          throw error
        }
      }
      // }
    },
    async showPlayerCardsForTeacher(playerId: string) {
      const userId = self.rootStore.session.user?._id.toString()
      const channelName = self.rootStore.communication.channel?.channelName
      if (channelName && userId) {
        try {
          await self.rootStore.api.mutateShowPlayerCardsForTeacher({
            command: {
              channelName,
              userId,
              playerId
            }
          })
        } catch (error) {
          logger.error('showPlayerCardsForTeacher API Error: ', error)
          throw error
        }
      }
      // }
    },
    async showPlayerCardsForEveryone(playerId: string) {
      const userId = self.rootStore.session.user?._id.toString()
      const channelName = self.rootStore.communication.channel?.channelName
      if (channelName && userId) {
        try {
          await self.rootStore.api.mutateShowPlayerCardsForEveryone({
            command: {
              channelName,
              userId,
              playerId
            }
          })
        } catch (error) {
          logger.error('showPlayerCardsForEveryone API Error: ', error)
          throw error
        }
      }
      // }
    },
    async hideCards() {
      const userId = self.rootStore.session.user?._id.toString()
      const channelName = self.rootStore.communication.channel?.channelName
      if (channelName && userId) {
        try {
          await self.rootStore.api.mutateHideCards({
            command: {
              channelName,
              userId
            }
          })
        } catch (error) {
          logger.error('hideCards API Error: ', error)
          throw error
        }
      }
      // }
    },
    async hidePlayerCards(playerId: string) {
      const userId = self.rootStore.session.user?._id.toString()
      const channelName = self.rootStore.communication.channel?.channelName
      if (channelName && userId) {
        try {
          await self.rootStore.api.mutateHidePlayerCards({
            command: {
              channelName,
              userId,
              playerId
            }
          })
        } catch (error) {
          logger.error('hidePlayerCards API Error: ', error)
          throw error
        }
      }
      // }
    },
    /**
     * Fetches public + community aggregated stats from GraphQL (MongoDB).
     * Convex practice + private stats are merged at the component level via useQuery.
     */
    async getPlayerGlobalStats(convexStats?: {
      numberOfHands: number
      numberOfHandsPlayed: number
      numberOfHandsWon: number
      preflopFold: number
      biggestWin: number
    }) {
      const userId = self.rootStore.session.user?._id.toString()
      if (userId) {
        try {
          const response = await self.rootStore.api.queryGetPlayerPublicCommunityStats(
            { userId },
            'numberOfHands numberOfHandsPlayed numberOfHandsWon preflopFold allTimeBiggestWin { chipsWon createdAt } allTimeBestHandWon { rankedHandName createdAt winningCards }',
            { fetchPolicy: 'no-cache' }
          )

          const gql = response.getPlayerPublicCommunityStats
          const cx = convexStats ?? { numberOfHands: 0, numberOfHandsPlayed: 0, numberOfHandsWon: 0, preflopFold: 0, biggestWin: 0 }

          const convexBiggestWin = cx.biggestWin ?? 0
          const gqlBiggestWin = gql.allTimeBiggestWin?.chipsWon ?? 0
          const allTimeBiggestWin = convexBiggestWin > gqlBiggestWin
            ? { chipsWon: convexBiggestWin }
            : gql.allTimeBiggestWin

          const merged = {
            _id: userId,
            numberOfHands: (gql.numberOfHands || 0) + (cx.numberOfHands || 0),
            numberOfHandsPlayed: (gql.numberOfHandsPlayed || 0) + (cx.numberOfHandsPlayed || 0),
            numberOfHandsWon: (gql.numberOfHandsWon || 0) + (cx.numberOfHandsWon || 0),
            preflopFold: (gql.preflopFold || 0) + (cx.preflopFold || 0),
            allTimeBiggestWin,
            allTimeBestHandWon: gql.allTimeBestHandWon
          }

          self.setMyPlayerStats(merged as PlayerStatsEntityModelType)
        } catch (error) {
          logger.error('getPlayerGlobalStats API Error: ', error)
          throw error
        }
      }
    },
    async getActiveOpenDefaultGame() {
      try {
        const response = await self.rootStore.api.mutateFindOrCreateOpenGame({}, '_id channelName')
        if (!response.findOrCreateOpenGame.channelName) {
          throw new Error('channelName undefined')
        }
        self.rootStore.communication.joinChannel(response.findOrCreateOpenGame.channelName)
        self.setGameKind(GameModeTag.openQuickGame)
        await self.rootStore.game.setupGame()
        // return response.findOrCreateOpenGame
      } catch (error) {
        logger.error('getActiveTournament API Error: ', error)
        throw error
      }
    },
    async getGameHistory() {
      // const userId = self.rootStore.session.user?._id.toString()
      // if (userId) {
      //   try {
      //     const response: { getGameHistory: PlayHistoryModelType[] } = await self.rootStore.api.queryGetGameHistory(
      //       { userId },
      //       { fetchPolicy: 'no-cache' }
      //     )
      //     console.log('getGameHistory: ', JSON.stringify(response))
      //     self.rootStore.game.setPlayHistoryData(response.getGameHistory)
      //   } catch (error) {
      //     console.error('getGameHistory API Error: ', error)
      //     throw error
      //   }
      // }
    },
    setIsFirstTime(value: boolean) {
      self.isFirstTime = value
    },

    resetTimer() {
      clearInterval(interval)
    },

    millisToMinutesAndSeconds(millis: number) {
      const seconds = Math.floor((millis / 1000) % 60)
      const minutes = Math.floor((millis / (1000 * 60)) % 60)
      return seconds + minutes * 60
    },

    async startBlindTimer() {
      this.resetTimer()
      const ms = new Date()
      const stime = self.gameStartTime // gameStartTimestamp
      const ctime = ms.getTime()
      const dtime = ctime - stime
      const blindTimeDuration = self.rootStore.game.blindTime
      let StartClockTime = 0
      let secondsLeft = Math.max(0, this.millisToMinutesAndSeconds(dtime))
      const blindIndex: number = Math.max(0, Math.floor(secondsLeft / blindTimeDuration)) //self.rootStore.game.blindLevel

      // if (self.isFirstTime == false) {
      //   blindIndex = (secondsLeft / blindTimeDuration)
      // }
      if (blindIndex > 0) {
        secondsLeft = secondsLeft - blindTimeDuration * blindIndex
      }
      StartClockTime = StartClockTime + secondsLeft

      self.rootStore.game.setIsFirstTime(false)
      self.rootStore.game.setRemainingTime(blindTimeDuration)
      self.rootStore.game.setPauseRemainingTime(0)
      interval = setInterval(async () => {
        if (self.isGameOver) {
          this.resetTimer()
          return
        }
        if (!(self.isGamePaused || self.isGameOver)) {
          if (Number(self.nextBlind.small) === 0) {
            self.rootStore.game.setRemainingTime(self.rootStore.game.blindTime)
          }
          const remaingTimeInSec = Math.abs(Math.max(0, blindTimeDuration - StartClockTime))

          if (remaingTimeInSec === 0 && StartClockTime !== 0) {
            this.resetTimer()
            StartClockTime = 0
            try {
              if (Number(self.nextBlind.small) !== 0) {
                self.rootStore.game.setRemainingTime(blindTimeDuration)
              }
            } catch (error) {
              logger.warn('[game] setInterval', error)
            }
          } else {
            self.rootStore.game.setRemainingTime(remaingTimeInSec)
            StartClockTime = StartClockTime + 1
            // if(self.isGameOver) {
            //   self.rootStore.game.resetBlindLevel()
            // }
          }
        }
      }, 1000)
    }
  }))
  .actions((self) => ({
    async editUserGameSchedule(
      hash: string,
      inputEditUserGameSchedule: InputEditUserGameSchedule
    ): Promise<UserGameScheduleResponseModelType> {
      try {
        const res = await self.rootStore.api.mutateEditUserGameSchedule({
          hash,
          inputEditUserGameSchedule
        })

        self.rootStore.game.setUserGameScheduleFullResponse(res.editUserGameSchedule)
        return res.editUserGameSchedule
      } catch (e) {
        console.log(e)
        throw e
      }
    },
    async getCanPlayThreeCard(): Promise<CanPlayThreeCardDailyGameModelType> {
      try {
        const { canPlayThreeCard } = await self.rootStore.api.queryCanPlayThreeCard({}, 'canPlay canShare', {
          fetchPolicy: 'no-cache'
        })
        self.setCanPlayThreeCard(canPlayThreeCard)
        return canPlayThreeCard
      } catch (error) {
        console.error('Error in fetching the three card result', error)
        throw error
      }
    },
    async getThreeCardResult(): Promise<void> {
      try {
        const userId = self.rootStore.session.user?._id
        if (userId) {
          const { getThreeCardResult } = await self.rootStore.api.queryGetThreeCardResult(
            { userId },
            'hand players { name chips cards } winners',
            { fetchPolicy: 'no-cache' }
          )

          self.setThreeCardResult(getThreeCardResult)
        }
      } catch (error) {
        console.error('Error in fetching the three card result', error)
      }
    },
    async setupGame() {
      // logger.log('setupGame')
      const store = self.rootStore
      try {
        // logger.log('setupGame GameScreen.useEffect joining channel and game...')

        const results = await Promise.allSettled([
          //This is used to show the delete game modal only
          store.game.fetchLastGameState(),
          store.messages.createChannelSubscription()
        ])

        // Handle results here
        results.forEach((result) => {
          if (result.status === 'rejected') {
            const statusCode = get(result.reason, 'response.errors[0].extensions.response.statusCode', 400)
            if (statusCode === 404) {
              self.rootStore.game.createGameDeletedAlert()
            }
          }
        })

        // logger.log('setupGame GameScreen.useEffect createChannelSubscription and setupVideo')
        // NOTE: the previous GraphQL subscription lib, need us to check in
        // We are leaving this here in case we experience issues with the newer graphql-ws lib
        // await checkConnection()
      } catch (error) {
        logger.error('setupGame GameScreen.useEffect error', error)
        throw error
      }
    }
  }))
  .actions((self) => ({
    async getPlayerGameStats(): Promise<PlayerGameStatsModelType> {
      const gameId = self.rootStore.game?.gameId

      const result = await self.rootStore.api.queryGetPlayerGameStats(
        { gameId },
        (qb) => playerGameStatsModelPrimitives.biggestWin(playerStatBiggestWinModelPrimitives),
        {
          fetchPolicy: 'no-cache'
        }
      )

      if (result.getPlayerGameStats) {
        self.setMyPlayerGameStats(result.getPlayerGameStats)
      }
      return result.getPlayerGameStats
    }
  }))
  .actions((self) => ({
    isPlayerCardsVisibleByTeacher(playerId: string): boolean {
      return !!(
        self.rootStore.game.isMyPlayerTeacher &&
        self.rootStore.game.isMyPlayerObserver &&
        self.rootStore.game.isPrivateGame &&
        self.players.find((p) => p.id === playerId)?.isCardsVisibleByTeacher
      )
    },
    isPlayerCardsVisibleByEveryone(playerId: string): boolean {
      return !!(
        self.rootStore.game.isPrivateGame && self.players.find((p) => p.id === playerId)?.isCardsVisibleByEveryone
      )
    }
  }))
  .actions((self) => {
    const getGameByHash = async (hash: string) => {
      const result = await self.rootStore.api.queryGetGameByHash({ hash }, (qb) =>
        gameEntityModelPrimitives.snapshot(gameSnapshotModelPrimitives).tournament(tournamentModelPrimitives)
      )

      return result.getGameByHash
    }

    const onChange = (nextAppState: AppStateStatus) => {
      // logger.info('app state onChange', nextAppState)
      self.handleAppStateChangeForGame(nextAppState)
    }

    const afterCreate = async () => {
      self.rootStore.game.clearGameSheetQueue()
      self.rootStore.game.clearGameModalQueue()
      self.rootStore.game.setShouldHighlightCurrentPlayer(!self.rootStore.game.isGameOver)
      self.modals.setPlayerActionTimerExpired(false)
      self.setHasUserTappedOnAction(false)
      self.rootStore.session.updateAppState('foregrounded')
      self.setShowPlayerTimerAfterJoinedGame(false)
      self.rootStore.game.setShowChats(false)
      self.rootStore.game.setChipsAnimationStarted(false)
      self.rootStore.game.setShouldPlayChipsSound(false)
      self.rootStore.game.setShowShareSheet(false)
      // self.rootStore.game.setShareSheet(undefined)
      self.rootStore.communication.clearCelebrations()
      self.rootStore.communication.clearShowCards()
      self.setShouldStartFetchGameStateInterval(false)
      self.setShouldCancelFetchGameStateInterval(false)
      self.rootStore.game.clearTargetPositions()
      self.rootStore.game.setIsReponseVisible(true)
      self.rootStore.session.setIsPNSheetOpen(false)
      // This initializes the current route name immediately.
      self.rootStore.game.setTimerCountDown(undefined)
      // const state = await storage.load(NAVIGATION_PERSISTENCE_KEY)
      // const routeName = getActiveRouteName(state as NavigationState)
      // self.rootStore.game.setRouteBeforeCallingJoinGame(routeName)
      self.rootStore.share.setIsShareOpen(false)
      self.rootStore.game.setPreviewVideoWasShown(false)
      self.rootStore.game.setIsHandWinnersEvent(false)
      self.rootStore.game.setIsBetModalOpen(false)
      self.rootStore.game.setIsCallOptionsModalOpen(false)
      self.rootStore.game.setIsGameStartSheetOpen(false)
      self.rootStore.game.setToast(null)
      self.rootStore.game.setTeachingTableRoster(undefined)
      if (!self.rootStore.network.isNetworkDown) self.rootStore.messages.clearSnack()

      setTimeout(() => {
        self.rootStore.game.setLandedGameScreenForFirstTime(true)
      }, 500)
    }

    const appStateListener = AppState.addEventListener('change', onChange)

    const cleanup = () => {
      appStateListener.remove()
    }

    const afterAttach = () => {
      self.rootStore.game.setTeachingTableRoster(undefined)
      self.wakeUp()
      // restore game state
      if (self.game) {
        logger.info('self.isPlayingGame', self.isPlayingGame)
        //Todo: Check the attributes and update
        //@ts-ignore
        self.setGame(self.game)
      }

      logger.debug('afterAttach')
    }

    const beforeDestroy = () => {
      logger.debug('beforeDestroy')
      cleanup()
    }

    return { afterCreate, afterAttach, beforeDestroy, getGameByHash }
  })

/**
 * The GameModel instance.
 */
export type GameModelType = Instance<typeof GameModel>

/**
 * The data of a GameModel.
 */
export type GameModelSnapshot = SnapshotOut<typeof GameModel>
